import type { TaskDelegationAudit } from 'prove-common'

export type DelegationHistoryChange =
  | {
      type: 'created'
      delegationId: number
      parentId: number
      ownerId: string
      quantity: number
    }
  | {
      type: 'removed'
      delegationId: number
      parentId: number
      ownerId: string
      quantity: number
    }
  | {
      type: 'quantity'
      delegationId: number
      parentId: number
      ownerId: string
      from: number
      to: number
    }
  | {
      type: 'owner'
      delegationId: number
      parentId: number
      ownerId: string
      previousOwnerId: string
    }

export type DelegationHistoryEvent = {
  key: string
  timestamp: number
  actorId: string
  actorName?: string
  changes: DelegationHistoryChange[]
}

type AuditChange = {
  audit: TaskDelegationAudit
  change: DelegationHistoryChange
}

const LEGACY_GROUP_WINDOW_MS = 5000

const timestampOf = (audit: TaskDelegationAudit): number =>
  new Date(audit.timestamp).getTime()

const changesFromAudit = (audits: TaskDelegationAudit[]): AuditChange[] => {
  const previousById = new Map<number, TaskDelegationAudit>()
  const changes: AuditChange[] = []

  const appendUpdates = (
    audit: TaskDelegationAudit,
    previous: TaskDelegationAudit
  ) => {
    if (previous.ownerId !== audit.ownerId) {
      changes.push({
        audit,
        change: {
          type: 'owner',
          delegationId: audit.id,
          parentId: audit.parentId,
          ownerId: audit.ownerId,
          previousOwnerId: previous.ownerId
        }
      })
    }
    if (previous.quantity !== audit.quantity) {
      changes.push({
        audit,
        change: {
          type: 'quantity',
          delegationId: audit.id,
          parentId: audit.parentId,
          ownerId: audit.ownerId,
          from: previous.quantity,
          to: audit.quantity
        }
      })
    }
  }

  for (const audit of audits.toSorted(
    (a, b) => timestampOf(a) - timestampOf(b)
  )) {
    const operation = audit.operation.toUpperCase()
    const previous = previousById.get(audit.id)
    if (operation === 'CURRENT') {
      if (
        previous != null &&
        previous.operation.toUpperCase() === 'UPDATE' &&
        previous.changeSetId == null
      ) {
        // Older triggers stored the state before an update. The synthetic
        // current row supplies the final after-image for the last update.
        appendUpdates(
          {
            ...audit,
            timestamp: previous.timestamp,
            changedByUserId: previous.changedByUserId,
            actorName: previous.actorName,
            changeSetId: previous.changeSetId
          },
          previous
        )
      }
      continue
    }

    if (operation === 'INSERT' || operation === 'CREATE') {
      changes.push({
        audit,
        change: {
          type: 'created',
          delegationId: audit.id,
          parentId: audit.parentId,
          ownerId: audit.ownerId,
          quantity: audit.quantity
        }
      })
      previousById.set(audit.id, audit)
      continue
    }

    if (operation === 'DELETE') {
      changes.push({
        audit,
        change: {
          type: 'removed',
          delegationId: audit.id,
          parentId: audit.parentId,
          ownerId: audit.ownerId,
          quantity: audit.quantity
        }
      })
      previousById.delete(audit.id)
      continue
    }

    if (operation === 'UPDATE' && previous != null) {
      appendUpdates(audit, previous)
    }
    previousById.set(audit.id, audit)
  }

  return changes
}

const toEvent = (
  key: string,
  changes: AuditChange[]
): DelegationHistoryEvent => {
  const latest = changes.toSorted(
    (a, b) => timestampOf(b.audit) - timestampOf(a.audit)
  )[0].audit
  const byDelegation = Map.groupBy(
    changes.map(({ change }) => change),
    (change) => change.delegationId
  )
  const ordered: DelegationHistoryChange[] = []
  const append = (delegationId: number) => {
    const delegationChanges = byDelegation.get(delegationId)
    if (delegationChanges == null) return
    ordered.push(...delegationChanges)
    byDelegation.delete(delegationId)
    for (const [childId, childChanges] of byDelegation) {
      if (childChanges[0].parentId === delegationId) append(childId)
    }
  }
  for (const [delegationId, delegationChanges] of byDelegation) {
    if (
      delegationChanges[0].parentId === delegationId ||
      !byDelegation.has(delegationChanges[0].parentId)
    ) {
      append(delegationId)
    }
  }
  for (const delegationId of [...byDelegation.keys()]) append(delegationId)

  return {
    key,
    timestamp: timestampOf(latest),
    actorId: latest.changedByUserId,
    actorName: latest.actorName,
    changes: ordered
  }
}

export const buildDelegationHistoryEvents = (
  audits: TaskDelegationAudit[]
): DelegationHistoryEvent[] => {
  const changes = changesFromAudit(audits)
  const correlated = Map.groupBy(
    changes.filter(({ audit }) => audit.changeSetId != null),
    ({ audit }) => audit.changeSetId!
  )
  const events = [...correlated.entries()].map(([id, grouped]) =>
    toEvent(`change-set-${id}`, grouped)
  )

  const legacy = changes
    .filter(({ audit }) => audit.changeSetId == null)
    .toSorted((a, b) => timestampOf(a.audit) - timestampOf(b.audit))
  let group: AuditChange[] = []
  for (const change of legacy) {
    const previous = group.at(-1)
    const sameActor =
      previous?.audit.changedByUserId === change.audit.changedByUserId
    const isClose =
      previous != null &&
      timestampOf(change.audit) - timestampOf(previous.audit) <=
        LEGACY_GROUP_WINDOW_MS
    if (group.length > 0 && (!sameActor || !isClose)) {
      events.push(toEvent(`legacy-${timestampOf(group[0].audit)}`, group))
      group = []
    }
    group.push(change)
  }
  if (group.length > 0) {
    events.push(toEvent(`legacy-${timestampOf(group[0].audit)}`, group))
  }

  return events.toSorted((a, b) => b.timestamp - a.timestamp)
}

export const summarizeDelegationHistoryEvent = (
  event: DelegationHistoryEvent
): string => {
  const affected = new Set(event.changes.map((change) => change.delegationId))
    .size
  const created = event.changes.filter(
    (change) => change.type === 'created'
  ).length
  const removed = event.changes.filter(
    (change) => change.type === 'removed'
  ).length
  const parts = [`${affected} ${affected === 1 ? 'enhet' : 'enheter'} endret`]
  if (created > 0) parts.push(`${created} lagt til`)
  if (removed > 0) parts.push(`${removed} fjernet`)
  return parts.join(' · ')
}
